const CONFIG = {
  CURRENCY: 'USD',
}

class Helpers {
  constructor() {
    this.currency = CONFIG.CURRENCY;
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency
    }).format(price);
  }

  formatDate(date) {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }
}

/**
 * Cart API
 * - Requires customer id and email to be set on the cart/buy-box form
 */
class CartApi {
  constructor() {
    this.routes = {
      cart: window.Shopify.routes.root + 'cart',
      cartAdd: window.Shopify.routes.root + 'cart/add.js',
      cartChange: window.Shopify.routes.root + 'cart/change.js',
      cartUpdate: window.Shopify.routes.root + 'cart/update.js',
      cartClear: window.Shopify.routes.root + 'cart/clear.js'
    }
    this.headers = {
      'Content-Type': 'application/json'
    }
  }

  // Helper method for fetch requests
  async fetchRequest(url, options = {}) {
    try {
      const response = await fetch(url, {
        headers: this.headers,
        ...options
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error in ${options.method || 'GET'} ${url}:`, error);
      throw error;
    }
  }

  /**
   * Add an item to the cart
   * @param {*} variantId 
   * @param {*} quantity 
   * @param {*} properties 
   * @param {*} cartAttributes 
   * @param {*} customerEmail 
   * @returns 
   */
  async addItem({
    variantId,
    quantity,
    properties,
    cartAttributes
  }) {
    const formData = {
      attributes: cartAttributes,
      items: [{
        id: variantId,
        quantity,
        properties
      }]
    };

    const response = await this.fetchRequest(this.routes.cartAdd, {
      method: 'POST',
      body: JSON.stringify(formData)
    });

    const cartData = await this.fetchCart();

    return cartData;
  }

  /**
   * Bulk add line items to the cart
   * @param {*} lineItems 
   * @param {*} cartAttributes (optional)
   * @returns 
   */
  async bulkAddLineItems({
    lineItems,
    cartAttributes
  }) {
    const formData = {
      items: lineItems,
      ...(cartAttributes && { attributes: cartAttributes })
    };

    const response = await this.fetchRequest(this.routes.cartAdd, {
      method: 'POST',
      body: JSON.stringify(formData)
    });

    const cart = await this.fetchCart();

    return cart;
  }

  /**
   * Fetch the cart
   * @returns 
   */
  async fetchCart() {
    return this.fetchRequest(this.routes.cart + '.js');
  }

  /**
   * Update a line item in the cart
   * @param {*} lineItemKey 
   * @param {*} data 
   * @returns 
   */
  async updateLineItem(lineItemKey, data) {
    const updates = { id: lineItemKey, ...data };
    await this.fetchRequest(this.routes.cartChange, {
      method: 'POST',
      body: JSON.stringify(updates)
    });
    return this.fetchCart();
  }

  /**
   * Update the cart
   * @param {*} cartId 
   * @param {*} data 
   * @returns 
   */
  async updateCart({
    attributes,
    lineItems
  }) {
    const formData = {
      attributes: attributes,
      updates: lineItems
    };
    return this.fetchRequest(this.routes.cartUpdate, {
      method: 'POST',
      body: JSON.stringify(formData)
    });
  }

  /**
   * Clear the cart
   * @returns 
   */
  async clearCart() {
    return this.fetchRequest(this.routes.cartClear, { method: 'POST' });
  }

  /**
   * Remove a line item from the cart
   * @param {*} lineItemKey 
   * @returns 
   */
  async removeLineItem(lineItemKey) {
    return this.updateLineItem(lineItemKey, { quantity: 0 });
  }
}

const helpers = new Helpers();
const cartApi = new CartApi();

/**
 * Multistep Form
 */
class MultistepForm extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.steps = [];
    this.elements = {};
  }

  init() {
    this.initialDisplay = this.steps[0].style.display || 'flex';
    this.showStep(this.currentStep);
    this.elements.form.addEventListener('submit', this.handleSubmit.bind(this));
    if (this.elements.backButton) {
      this.elements.backButton.addEventListener('click', this.handleBack.bind(this));
    }
    if (this.elements.nextButton) {
      this.elements.nextButton.addEventListener('click', this.handleNext.bind(this));
      // Disable next button by default
      this.elements.nextButton.disabled = true;
    }
    if (this.elements.submitButton) {
      // Disable submit button by default
      this.elements.submitButton.disabled = true;
    }
    // Listen for input changes for validation
    this.steps.forEach((step) => {
      step.querySelectorAll('input, select, textarea').forEach((el) => {
        el.addEventListener('input', this.handleInputChange.bind(this));
        el.addEventListener('change', this.handleInputChange.bind(this));
        el.addEventListener('blur', this.handleInputChange.bind(this));
      });
      
      // Special handling for multiselect components
      step.querySelectorAll('[data-input-multiselect], .input-multiselect-block').forEach((multiselect) => {
        
        // Listen for checkbox changes in multiselect
        const checkboxes = multiselect.querySelectorAll('input[type="checkbox"]');
        checkboxes.forEach((checkbox) => {
          checkbox.addEventListener('change', (e) => {
            this.handleInputChange();
          });
          checkbox.addEventListener('click', (e) => {
            this.handleInputChange();
          });
        });
        
        // Listen for clicks on the label elements (which contain the visual buttons)
        const labels = multiselect.querySelectorAll('.multiselect-btn-label');
        labels.forEach((label) => {
          label.addEventListener('click', (e) => {
            // Use setTimeout to ensure the checkbox state has updated
            setTimeout(() => {
              this.handleInputChange();
            }, 10);
          });
        });
        
        // Listen for custom events that multiselect might emit
        multiselect.addEventListener('selection-changed', (e) => {
          this.handleInputChange();
        });
        multiselect.addEventListener('value-changed', (e) => {
          this.handleInputChange();
        });
        
        // Also listen for clicks on multiselect options
        const options = multiselect.querySelectorAll('[data-option], .option, .multiselect-option, .multiselect-btn-label');
        options.forEach((option) => {
          option.addEventListener('click', (e) => {
            setTimeout(() => {
              this.handleInputChange();
            }, 10);
          });
        });
        
        // Listen for changes on any hidden input that multiselect might use
        const hiddenInputs = multiselect.querySelectorAll('input[type="hidden"]');
        hiddenInputs.forEach((input) => {
          input.addEventListener('change', (e) => {
            this.handleInputChange();
          });
        });
      });
      
      // Special handling for dropdown components
      step.querySelectorAll('select').forEach((dropdown) => {
        
        // Listen for change events on dropdowns
        dropdown.addEventListener('change', (e) => {
          this.handleInputChange();
        });
        
        // Also listen for input events for immediate feedback
        dropdown.addEventListener('input', (e) => {
          this.handleInputChange();
        });
      });
    });
    this.updateNavigationState();
  }

  showStep(index) {
    this.steps.forEach((step, i) => {
      step.style.display = i === index ? this.initialDisplay : 'none';
    });
    // Update navigation buttons
    if (this.elements.backButton) {
      if (index === 0) {
        this.elements.backButton.closest('.shopify-block').style.display = 'none';
      } else {
        this.elements.backButton.closest('.shopify-block').style.display = 'block';
      }
    }
    if (this.elements.nextButton) {
      if (index === this.steps.length - 1) {
        this.elements.nextButton.closest('.shopify-block').style.display = 'none';
      } else {
        this.elements.nextButton.closest('.shopify-block').style.display = 'block';
      }
    }
    if (this.elements.submitButton) {
      if (index === this.steps.length - 1) {
        this.elements.submitButton.closest('.shopify-block').style.display = 'block';
      } else {
        this.elements.submitButton.closest('.shopify-block').style.display = 'none';
      }
    }
    this.updateNavigationState();
  }

  handleNext(event) {
    event.preventDefault();
    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      this.showStep(this.currentStep);
    }
  }

  handleBack(event) {
    event.preventDefault();
    if (this.currentStep > 0) {
      this.currentStep--;
      this.showStep(this.currentStep);
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    // Get the form data
    const formData = new FormData(this.elements.form);
    
    // Convert formData to an object, properly handling multiselect values
    const formDataObject = {};
    for (let [key, value] of formData.entries()) {
      const formattedValue = value.replace(/\n/g, '').trim();
      if (formDataObject[key]) {
        // If this key already exists, convert to array and strip any newlines
        if (Array.isArray(formDataObject[key])) {
          formDataObject[key].push(formattedValue);
        } else {
          formDataObject[key] = [formDataObject[key], formattedValue];
        }
      } else {
        formDataObject[key] = formattedValue;
      }
    }
    
    console.log('formDataObject', formDataObject);
    if (this.currentStep < this.steps.length - 1) {
      this.handleNext(event);
    } else {
      // Allow submit on last step if valid
      if (!this.isStepValid(this.currentStep)) {
        this.updateNavigationState();
        return;
      }

      // Submit to Google Sheets
      this.submitToGoogleSheets(formDataObject);
      
      // Submit to Klaviyo
      this.submitToKlaviyo(formDataObject);

      // Show the thank you message
      this.showSuccessMessage();
    }
  }

  async submitToGoogleSheets(data) {

    const googleSheetUrl = 'https://script.google.com/macros/s/AKfycbzKRKejpoPt9nTw-58QktidB1xeNw1F3StXO97eETbX2UKW4tmvijqpNojDOARbZcx1/exec';

    const flattenedData = {};

    // Flatten the data object
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'object' && value !== null) {
        // Handle objects with nested values
        if (Array.isArray(value)) {
          flattenedData[key] = value.join(', ');
        } else if (typeof value === 'object') {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            flattenedData[`${key}_${nestedKey}`] = nestedValue ? 'Yes' : 'No';
          });
        } else {
          flattenedData[key] = JSON.stringify(value);
        }
      } else {
        flattenedData[key] = String(value);
      }
    });

    // Include any UTM parameters from sessionStorage
    const storedUtmParams = sessionStorage.getItem('utmParameters');
    if (storedUtmParams) {
      const utmParams = JSON.parse(storedUtmParams);
      Object.entries(utmParams).forEach(([key, value]) => {
        flattenedData[key] = String(value);
      });
    }

    console.log('Flattened data for Google Sheets:', flattenedData);

    // Build a FormData object for the fetch request
    const formData = new FormData();
    Object.entries(flattenedData).forEach(([key, value]) => {
      formData.append(key, value);
    });

    console.log('Submitting form data using fetch POST request');

    // Use fetch to make the POST request with no-cors mode to bypass CORS issues
    // Note: This means we won't be able to read the response, but the data will still be submitted
    await fetch(googleSheetUrl, {
      method: 'POST',
      body: formData, // Do not manually set Content-Type
      // mode: 'no-cors',
    })
      .then(async (response) => {
        console.log('Response from Google Sheets:', response);
        const responseText = await response.text();
        console.log('Response text:', responseText);
      })
      .catch((error) => {
        console.error('Error submitting form data:', error);
      });
    
    return true;
  }

  async submitToKlaviyo(formData) {
    const email = formData.email;
    const source = formData.source || 'multistep_form';
    const zipCode = formData.zip_code;
    const klaviyoListId = this.section.getAttribute('data-klaviyo-list-id');
    
    if (!email || !klaviyoListId) {
      console.error('[multistep-form] Missing email or Klaviyo list ID');
      return;
    }

    // Prepare profile properties from form data
    const profileProperties = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'email' && key !== 'source' && key !== 'zip_code' && key !== 'form_type' && key !== 'utf8' && key !== 'shop' && key !== 'customer') {
        profileProperties[key] = formData[key];
      }
    });

    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=VAYLQf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2025-04-15',
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: source,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    location: zipCode ? {
                      zip: zipCode,
                    } : undefined,
                    properties: {
                      ...profileProperties,
                      ...(zipCode && { zip_code: zipCode }),
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: klaviyoListId,
                },
              },
            },
          },
        }),
      });

      if (response.ok) {
        console.log('[multistep-form] Success! Form submitted to Klaviyo.');
        // Set localStorage to track the user's subscription status in this browser session
        localStorage.setItem('klaviyo_subscribed', 'true', { expires: 365 });
        
        // Show success message or redirect
        this.showSuccessMessage();
      } else {
        const errorData = await response.json();
        console.error('[multistep-form] Klaviyo error:', errorData);
        this.showErrorMessage();
      }
    } catch (error) {
      console.error('[multistep-form] Klaviyo error:', error);
      this.showErrorMessage();
    }
  }

  showSuccessMessage() {
    if (this.elements.successMessageContent) {
      this.elements.successMessageContent.style.display = 'flex';
      this.elements.successMessage.classList.remove('tw-hidden');
      this.elements.form.style.display = 'none';

      // Set scroll position to the top of the form section
      window.scrollTo({
        top: this.elements.formSection.offsetTop,
        behavior: 'smooth'
      });
    }
  }

  showErrorMessage() {
    const errorMessage = this.elements.errorMessage;
    if (errorMessage) {
      errorMessage.style.display = 'flex';
    } else {
      // Create a default error message if none exists
      const defaultMessage = document.createElement('div');
      defaultMessage.className = 'multistep-form__error-message tw-flex tw-flex-col tw-gap-4 tw-text-center tw-max-w-lg tw-mt-4 tw-text-red-600';
      defaultMessage.innerHTML = '<p class="tw-text-lg">There was an error submitting your form. Please try again.</p>';
      this.section.appendChild(defaultMessage);
    }
  }

  handleInputChange() {
    this.updateNavigationState();
  }

  isStepValid(stepIndex) {
    const step = this.steps[stepIndex];
    if (!step) return false;
    
    let valid = true;
    
    // Only check visible/required fields
    const fields = step.querySelectorAll('input, select, textarea');
    fields.forEach((field) => {
      // For multiselects, we'll handle them separately
      if (field.closest('[data-input-multiselect]')) {
        // Skip individual multiselect checkboxes - we'll validate the container
        return;
      } else if (field.required && field.offsetParent !== null) {
        if (!field.checkValidity()) {
          valid = false;
        }
      }
    });

    // Special validation for multiselect components
    const multiselects = step.querySelectorAll('[data-input-multiselect]');
    multiselects.forEach((multiselect) => {
      const isRequired = multiselect.hasAttribute('required') || 
                        multiselect.classList.contains('required') ||
                        multiselect.getAttribute('data-required') === 'true';

      if (isRequired && multiselect.offsetParent !== null) {
        // Check if multiselect has selected values
        const hasSelectedValues = this.checkMultiselectValue(multiselect);
        if (!hasSelectedValues) {
          valid = false;
        }
      }
    });
    
    return valid;
  }
  
  checkMultiselectValue(multiselect) {
    
    // Get all checkboxes in this multiselect
    
    // Check for checked checkboxes (primary method for this implementation)
    const checkedCheckboxes = multiselect.querySelectorAll('input[type="checkbox"]:checked');
    
    if (checkedCheckboxes.length > 0) {
      // Collect all selected values
      return true;
    }
    
    // Check for selected options
    const selectedOptions = multiselect.querySelectorAll('[data-option].selected, .option.selected, .multiselect-option.selected');
    if (selectedOptions.length > 0) {
      return true;
    }
    
    // Check for hidden inputs with values
    const hiddenInputs = multiselect.querySelectorAll('input[type="hidden"]');
    for (let input of hiddenInputs) {
      if (input.value && input.value.trim() !== '') {
        return true;
      }
    }
    
    // Check for data attributes that might store selected values
    const selectedValues = multiselect.getAttribute('data-selected-values');
    if (selectedValues && selectedValues.trim() !== '') {
      return true;
    }
    
    return false;
  }

  updateNavigationState() {
    const isValid = this.isStepValid(this.currentStep);
    if (this.elements.nextButton && this.currentStep < this.steps.length - 1) {
      this.elements.nextButton.disabled = !isValid;
    }
    if (this.elements.submitButton && this.currentStep === this.steps.length - 1) {
      this.elements.submitButton.disabled = !isValid;
    }
  }

  connectedCallback() {
    this.section = this;
    this.elements = {
      form: this.section.querySelector('.multistep-form__form'),
      formSection: this.section.querySelector('.multistep-form__form').closest('section'),
      stepContent: this.section.querySelector('.multistep-form__step-content'),
      stepNavigation: this.section.querySelector('.multistep-form__step-navigation'),
      backButton: this.section.querySelector('[data-block-id$="__multistep-back-button"] button'),
      nextButton: this.section.querySelector('[data-block-id$="__multistep-next-button"] button'),
      submitButton: this.section.querySelector('[data-block-id$="__multistep-submit-button"] button'),
      successMessage: this.section.querySelector('.multistep-form__success-message'),
      successMessageContent: this.section.querySelector('.multistep-form__success-message .shopify-block'),
      errorMessage: this.section.querySelector('.multistep-form__error-message'),
      errorMessageContent: this.section.querySelector('.multistep-form__error-message .shopify-block')
    };
    this.steps = Array.from(this.elements.stepContent.children);
    this.init();
  }

  disconnectedCallback() {
    this.elements.form.removeEventListener('submit', this.handleSubmit.bind(this));
    if (this.elements.backButton) {
      this.elements.backButton.removeEventListener('click', this.handleBack.bind(this));
    }
    if (this.elements.nextButton) {
      this.elements.nextButton.removeEventListener('click', this.handleNext.bind(this));
    }
    // Remove input listeners
    this.steps.forEach((step) => {
      step.querySelectorAll('input, select, textarea').forEach((el) => {
        el.removeEventListener('input', this.handleInputChange.bind(this));
        el.removeEventListener('change', this.handleInputChange.bind(this));
        el.removeEventListener('blur', this.handleInputChange.bind(this));
      });
    });
  }
}

/**
 * Custom Elements
 */
customElements.define('multistep-form', MultistepForm);

/**
 * Listeners
 */
const listeners = {
  init() {
    document.addEventListener('DOMContentLoaded', () => {

      initModal();

      document.querySelectorAll('[data-collapsible]').forEach(card => {
        const trigger = card.querySelector('[data-collapse-trigger]');
        const content = card.querySelector('[data-collapse-content]');
        const icon = card.querySelector('[data-collapse-icon]');

        // Set initial state
        if (card.dataset.collapsed === 'true') {
          content.style.display = 'none';
          icon.style.transform = 'rotate(-180deg)';
        }

        trigger.addEventListener('click', () => {
          const isCollapsed = content.style.display === 'none';
          content.style.display = isCollapsed ? 'block' : 'none';
          icon.style.transform = isCollapsed ? 'rotate(0)' : 'rotate(-180deg)';
        });
      });
    });
  }
}

// Modal
function initModal() {
  if (!customElements.get('modal-component')) {
    customElements.define(
      'modal-component',
      class extends HTMLElement {
        constructor() {
          super();

          // Handle opening of modal
          const triggers = document.querySelectorAll('[data-modal-trigger]');
          triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => {
              const modalContentTarget = trigger.getAttribute('data-modal-content-target');
              const modalContent = document.querySelector(modalContentTarget);
              this.innerHTML = modalContent.innerHTML;
              this.classList.toggle('tw-hidden');
            });
          });
        }

        connectedCallback() {
          this.addEventListener('click', this.handleClickOutside);
        }

        handleClickOutside(event) {
          if (event.target.tagName === 'MODAL-COMPONENT') {
            this.innerHTML = '';
            this.classList.toggle('tw-hidden');
          }
        }
      }
    );
  }
}


/**
 * Initialize listeners
 */
listeners.init();